// "Make a task" on a line of a transcript: the line becomes a task in a project (the meeting's own project offered
// first), and the task keeps the words and where they were said. Only the team sees that part of it.
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../../lib/api";
import { projectKeys, type ProjectSummary, type Task } from "../../lib/projects";
import { Alert, Button, Dialog, Select, Spinner } from "../../components/ui";
import { Icon } from "../../components/icons";
import { atLeast, errorText, useOrg } from "../org";

type Line = { id: string; speakerName: string; startMs: number; body: string };

export function MakeTaskFromLine({ transcriptId, line, at }: { transcriptId: string; line: Line; at: string }) {
  const { role } = useOrg();
  const [open, setOpen] = useState(false);
  if (!atLeast(role, "member")) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label={`Make a task from what ${line.speakerName} said at ${at}`}
        className="shrink-0 self-start rounded-md p-1.5 text-slate-400 opacity-0 transition group-hover:opacity-100 hover:bg-brand-50 hover:text-brand-700 focus:opacity-100 [@media(hover:none)]:opacity-100 dark:hover:bg-brand-700/30">
        <Icon name="check" className="h-4 w-4" />
      </button>
      {open && <MakeTaskDialog transcriptId={transcriptId} line={line} at={at} onClose={() => setOpen(false)} />}
    </>
  );
}

function MakeTaskDialog({ transcriptId, line, at, onClose }: { transcriptId: string; line: Line; at: string; onClose: () => void }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const projects = useQuery({ queryKey: [...projectKeys.all(org.slug), "to-pick"], queryFn: () => api<{ projects: ProjectSummary[] }>(`/orgs/${org.slug}/projects`).then((r) => r.projects) });
  const home = useQuery({
    queryKey: ["project-of-transcript", org.slug, transcriptId],
    queryFn: () => api<{ project: { id: string } | null }>(`/orgs/${org.slug}/projects/of-transcript/${transcriptId}`).then((r) => r.project),
  });
  const [projectId, setProjectId] = useState<string | null>(null);
  const [title, setTitle] = useState(line.body.slice(0, 300));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ task: Task; projectId: string } | null>(null);
  const mine = (projects.data ?? []).filter((p) => p.myRole !== "client");
  const chosen = projectId ?? home.data?.id ?? mine[0]?.id ?? "";

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await api<{ task: Task }>(`/orgs/${org.slug}/projects/${chosen}/tasks`, {
        method: "POST", body: { title: title.trim(), source: { transcriptId, segmentId: line.id } },
      });
      await qc.invalidateQueries({ queryKey: projectKeys.tasks(org.slug, chosen) });
      setMade({ task: r.task, projectId: chosen });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 402 ? err.message : errorText(err));
    } finally { setBusy(false); }
  }

  return (
    <Dialog open onClose={onClose} title="Make a task">
      {made ? (
        <div className="space-y-4">
          <Alert kind="success">{made.task.ref} is on the board: {made.task.title}</Alert>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Back to the transcript</Button>
            <Link to={`/o/${org.slug}/projects/${made.projectId}?task=${made.task.id}`}><Button>Open the task</Button></Link>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          <blockquote className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
            <span className="font-medium">{line.speakerName}</span> <span className="text-slate-400">at {at}</span>
            <span className="mt-1 block">"{line.body}"</span>
          </blockquote>
          {projects.isLoading || home.isLoading ? <div className="flex justify-center p-4 text-brand-600"><Spinner /></div> : mine.length === 0 ? (
            <Alert kind="info">There's no project to put it in yet. <Link to={`/o/${org.slug}/projects`} className="underline">Make one first</Link>.</Alert>
          ) : (
            <>
              <Select label="Project" value={chosen} onChange={(e) => setProjectId(e.target.value)}>
                {mine.map((p) => <option key={p.id} value={p.id}>{p.name}{p.id === home.data?.id ? " (this meeting's project)" : ""}</option>)}
              </Select>
              <div>
                <label htmlFor="task-from-line" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Task</label>
                <textarea id="task-from-line" rows={3} value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
                <p className="mt-1.5 text-xs text-slate-500">The task remembers this line and links back to it. A client never sees the line, only the task.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
                <Button type="submit" loading={busy} disabled={!title.trim() || !chosen}>Make the task</Button>
              </div>
            </>
          )}
        </form>
      )}
    </Dialog>
  );
}
